/**
 * Cookie utilities for auth (role, active_artist_slug).
 * Note: access/refresh may be httpOnly (set by backend) and not readable from JS.
 */

export const AUTH_COOKIE_NAMES = {
  ACCESS: 'access',
  REFRESH: 'refresh',
  ACTIVE_ARTIST_SLUG: 'active_artist_slug',
  ROLE: 'role',
} as const;

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

export function setCookie(name: string, value: string, options?: { maxAge?: number; path?: string }): void {
  if (typeof document === 'undefined') return;
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  cookie += '; path=' + (options?.path ?? '/');
  if (options?.maxAge != null) cookie += `; max-age=${options.maxAge}`;
  document.cookie = cookie;
}

export function deleteCookie(name: string, path = '/'): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${encodeURIComponent(name)}=; path=${path}; max-age=0`;
}

export interface AuthCookies {
  access: string | null;
  refresh: string | null;
  active_artist_slug: string | null;
  role: string | null;
}

export function getAuthCookies(): AuthCookies {
  return {
    access: getCookie(AUTH_COOKIE_NAMES.ACCESS),
    refresh: getCookie(AUTH_COOKIE_NAMES.REFRESH),
    active_artist_slug: getCookie(AUTH_COOKIE_NAMES.ACTIVE_ARTIST_SLUG),
    role: getCookie(AUTH_COOKIE_NAMES.ROLE),
  };
}

/** Check if access and refresh tokens are present in cookies */
export function hasAccessAndRefreshCookies(): boolean {
  const { access, refresh } = getAuthCookies();
  return !!(access && refresh);
}

/** Check if auth cookies indicate a valid session (backend sets access/refresh/active_artist_slug) */
export function hasValidAuthCookies(): boolean {
  const { access, refresh, active_artist_slug } = getAuthCookies();
  return !!(access && refresh && active_artist_slug);
}

/** Set role in cookies (we set from frontend; backend sets access/refresh/active_artist_slug) */
export function setAuthRoleCookies(role: string): void {
  setCookie(AUTH_COOKIE_NAMES.ROLE, role);
}
